import React, { useCallback, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import BackButtonLink from "components/Common/buttons/BackButtonLink";
import classnames from "classnames";
import tasksIcon from "../../../../assets/images/svg/tasks.svg";
import lockIcon from "../../../../assets/images/svg/Lock.svg";
import { USER_ROLES } from "utils/constants";
import SendCommandsModal from "pages/meter-management/meters/meterDetails/SendCommandsModal";
import UpdateFirmwareModal from "pages/meter-management/meters/UpdateFirmwareModal";
import ButtonGroupContainer from "components/Common/buttons/ButtonGroupContainer";
import ButtonGroup from "components/Common/buttons/ButtonGroup";
import mailIcon from "assets/images/svg/mail.svg";
import updateIcon from "assets/images/svg/firmware-update.svg";
import openLock from "assets/images/svg/Open Lock.svg";
import CustomButton from "components/Common/buttons/CustomButton";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { removeFromQuarantine } from "utils/api";
import { defaultNgiErrorMethod } from "components/Common/responses/message";
import MeterQuarantineModal from "pages/meter-management/awaitingTestingMeters/MeterQuarantineModal";

const useStyles = makeStyles({
  root: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  imageContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: 15,
  },
  button: {
    marginLeft: 10,
  },
  mainInfo: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 8px 16px 0 #00000014",
    borderRadius: 100,
    height: 60,
    marginRight: 20,
    marginLeft: 20,
  },
  nameContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    marginLeft: 23,

    "& span": {
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 4,
    },
  },
  status: {
    color: "#FFFFFF",
    fontSize: 9,
    fontWeight: 300,
    backgroundColor: "#17C347",
    textTransform: "uppercase",
    padding: "3px 8px",
    borderRadius: 6,
    letterSpacing: "0.05em",
  },
  inactive: {
    backgroundColor: "#c31d17",
  },
  section: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 20px",
    maxWidth: 220,

    "& img": {
      width: 20,
      marginRight: 10,
    },

    "& button": {
      marginLeft: 15,
    },
  },
  title: {
    color: "#666666",
    fontSize: 9,
    fontWeight: 300,
  },
  data: {
    fontSize: 11,
    fontWeight: 600,
  },
  rightContainer: {
    display: "flex",
    marginTop: 15,
  },
  quarantine: {
    color: "#DB6607",
  },
});

const Header = ({ data, onUpdate, setLoader }) => {
  const classes = useStyles();
  const [isSendCommandOpen, setIsSendCommandOpen] = useState(false);
  const [isUpdateFirmwareOpen, setIsUpdateFirmwareOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isMeterQuarantineOpen, setIsMeterQuarantineOpen] = useState(false);

  const openSendCommandModal = useCallback(() => setIsSendCommandOpen(true), []);
  const closeSendCommandModal = useCallback(() => setIsSendCommandOpen(false), []);

  const openUpdateFirmwareModal = useCallback(() => setIsUpdateFirmwareOpen(true), []);
  const closeUpdateFirmwareModal = useCallback(() => setIsUpdateFirmwareOpen(false), []);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const onRemove = useCallback(() => {
    closeConfirmationModal();
    setLoader(true);
    removeFromQuarantine([data.id])
      .then(() => {
        setIsMeterQuarantineOpen(true);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [data]);

  const closeMeterQuarantineModal = useCallback(() => {
    onUpdate();
    setIsMeterQuarantineOpen(false);
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.imageContainer}>
        <BackButtonLink to={"/meters/meters"}>Back</BackButtonLink>
        <div className={classes.nameContainer}>
          <span>{data?.barcode}</span>
          <div className={classnames(classes.status, data?.status === "Inactive" && classes.inactive)}>
            {data?.status}
          </div>
        </div>
      </div>
      <div className={classes.rightContainer}>
        <div className={classes.mainInfo}>
          <div className={classes.section}>
            <img src={data?.condition === "Quarantine" ? lockIcon : tasksIcon} alt="meter" />
            <div>
              <div className={classes.title}>METER CONDITION</div>
              <div className={classnames(classes.data, data?.condition === "Quarantine" && classes.quarantine)}>
                {data?.condition}
              </div>
            </div>
            {data?.condition === "Quarantine" && (
              <CustomButton
                roles={[USER_ROLES.PRODUCT_TECHNICIAN_LEAD]}
                onClick={openConfirmationModal}
                isButtonRound
                size="small"
                iconImg={openLock}
                type="primary"
              />
            )}
          </div>
        </div>
        <div className="filter-buttons">
          <ButtonGroupContainer color={"primary"}>
            <ButtonGroup
              hideRoles={[
                USER_ROLES.INTERNAL_AUDITOR,
                USER_ROLES.GENERAL,
                USER_ROLES.FINANCE,
                USER_ROLES.PRODUCT_TECHNICIANS,
                USER_ROLES.PRODUCT_TECHNICIAN_LEAD,
                USER_ROLES.IT_OPERATIONS,
                USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                USER_ROLES.MDSC,
              ]}
              color={"primary"}
              handleClick={openSendCommandModal}
              tooltipTitle="Send Commands"
              icon={mailIcon}
            />
            <ButtonGroup
              roles={[
                USER_ROLES.METER_ENGINEER_LEADERSHIP,
                USER_ROLES.METER_ENGINEER,
                USER_ROLES.PRODUCT_TECHNICIAN_LEAD,
              ]}
              color={"primary"}
              handleClick={openUpdateFirmwareModal}
              tooltipTitle="Update Firmware"
              icon={updateIcon}
            />
          </ButtonGroupContainer>
        </div>
      </div>
      {isUpdateFirmwareOpen && (
        <UpdateFirmwareModal
          onUpdate={onUpdate}
          isOpen={isUpdateFirmwareOpen}
          activeItem={data}
          onClose={closeUpdateFirmwareModal}
        />
      )}
      {isSendCommandOpen && (
        <SendCommandsModal
          onClose={closeSendCommandModal}
          isPX={data.type === "PX-MQTT"}
          meterType={data.type}
          meterId={data.id}
        />
      )}
      {isConfirmationOpen && (
        <ConfirmationModal
          description="Are you sure you want to remove the meter from quarantine?"
          onCancel={closeConfirmationModal}
          onConfirm={onRemove}
        />
      )}
      {isMeterQuarantineOpen && (
        <MeterQuarantineModal isRemove serial={data.serial} onClose={closeMeterQuarantineModal} />
      )}
    </div>
  );
};

export default Header;
